import React, { useState, useEffect } from 'react';
import { auth } from '../../core/firebase/config';
import { sendEmailVerification, signInWithEmailAndPassword, updateProfile } from 'firebase/auth';
import MintLeafLogo from '../../../components/icons/AppleLogo';
import ArrowIcon from '../../../components/icons/ArrowIcon';
import EyeIcon from '../../../components/icons/EyeIcon';
import EyeSlashIcon from '../../../components/icons/EyeSlashIcon';
import { NICKNAME_TAKEN, saveNicknameForUser } from '../../core/auth/authHelpers';
import { enqueueQueuedEmail } from '../../core/services/emailQueueService';
import { getInviteForConsume, registerInvite } from '../../core/services/inviteApiService';

interface RegisterProps {
  inviteCode: string;
  onRegisterSuccess: () => void;
}

const Register: React.FC<RegisterProps> = ({ inviteCode, onRegisterSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [lastName, setLastName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [username, setUsername] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [inviteDetails, setInviteDetails] = useState<{ 
      role: string; 
      unitId: string; 
      position: string; 
    } | null>(null);

  useEffect(() => {
    const fetchInviteDetails = async () => {
      try {
        const data = await getInviteForConsume(inviteCode);
        setInviteDetails({
          role: data?.role || 'User',
          unitId: data?.unitId || '',
          position: data?.position || '',
        });
      } catch (err: any) {
        if (err?.statusCode === 404) {
          setError('Érvénytelen meghívó kód.');
        } else if (err?.statusCode === 400 || err?.statusCode === 409) {
          setError('A meghívó lejárt vagy már felhasznált.');
        } else {
          setError('Hiba a meghívó adatainak lekérésekor.');
        }
        console.error(err);
      }
    };
    fetchInviteDetails();
  }, [inviteCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('A két jelszó nem egyezik.');
      return;
    }
    if (!inviteDetails) {
        setError('A meghívó adatai még töltődnek, vagy érvénytelenek.');
        return;
    }
    setIsLoading(true);
    setError('');

    try {
      const trimmedUsername = username.trim();
      const normalizedEmail = email.trim();
      const normalizedFirstName = firstName.trim();
      const normalizedLastName = lastName.trim();
      const result = await registerInvite({
        code: inviteCode,
        registrationData: {
          email: normalizedEmail,
          password,
          firstName: normalizedFirstName,
          lastName: normalizedLastName,
          username: trimmedUsername,
        },
      });

      if (result?.success === false) {
        setError(result?.message || 'A meghívó feldolgozása nem sikerült.');
        setIsLoading(false);
        return;
      }

      if (result?.mode === 'claim_existing') {
        console.info('Invite register completed in claim_existing mode.', {
          inviteId: result.inviteId,
          userId: result.userId,
          idempotent: result.idempotent === true,
        });
      } else if (result?.mode === 'create') {
        console.info('Invite register completed in create mode.', {
          inviteId: result.inviteId,
          userId: result.userId,
          idempotent: result.idempotent === true,
        });
      }

      if (result?.idempotent === true) {
        console.info('Invite register returned idempotent success.', {
          inviteId: result.inviteId,
          userId: result.userId,
          mode: result.mode,
        });
      }

      const isClaimExisting = result?.mode === 'claim_existing';
      const isIdempotent = result?.idempotent === true;

      const signedIn = await signInWithEmailAndPassword(auth, normalizedEmail, password);
      if (signedIn.user && !isClaimExisting) {
        await saveNicknameForUser(signedIn.user.uid, normalizedEmail, trimmedUsername);
        await updateProfile(signedIn.user, {
          displayName: `${normalizedLastName} ${normalizedFirstName}`.trim(),
        });
        await sendEmailVerification(signedIn.user);
      }

      if (!isIdempotent) {
        try {
          await enqueueQueuedEmail('register_welcome', null, {
            name: normalizedFirstName,
            email: normalizedEmail,
          });
        } catch (emailError) {
          console.error('Failed to enqueue register_welcome email after registration success:', emailError);
        }
      }

      onRegisterSuccess();
    } catch (err: any) {
      if (err?.statusCode === 404) {
        setError('Érvénytelen meghívó kód.');
        setIsLoading(false);
        return;
      }
      if (err?.statusCode === 409) {
        setError('A meghívó már felhasználásra került.');
        setIsLoading(false);
        return;
      }
      if (err?.statusCode === 400) {
        setError('A meghívó lejárt vagy érvénytelen.');
        setIsLoading(false);
        return;
      }

      switch (err.code) {
        case NICKNAME_TAKEN:
          setError('Ez a felhasználónév már foglalt.');
          break;
        case 'auth/email-already-in-use':
          setError('Ezzel az email címmel már regisztráltak.');
          break;
        case 'auth/invalid-credential':
        case 'auth/user-not-found':
        case 'auth/wrong-password':
          setError('A meghívó feldolgozása sikerült, de a bejelentkezés nem sikerült. Kérd az admint, hogy ellenőrizze az account összekötést.');
          break;
        case 'auth/invalid-email':
          setError('Érvénytelen email formátum.');
          break;
        case 'auth/weak-password':
          setError('A jelszó túl gyenge. Legalább 6 karakter hosszú legyen.');
          break;
        default:
          setError('Hiba a regisztráció során. Próbáld újra később.');
          console.error(err);
          break;
      }
      setIsLoading(false);
    }
  };

  if (!inviteDetails && !error) {
    return (
        <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl w-full max-w-sm p-8 text-center">
             <div className="flex justify-center mb-4"><MintLeafLogo /></div>
             <p>Meghívó ellenőrzése...</p>
        </div>
    );
  }

  return (
    <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl w-full max-w-sm p-8 text-center">
      <div className="flex justify-center mb-4">
        <MintLeafLogo />
      </div>
      
      <h1 className="text-3xl font-bold text-gray-800 mb-1">MintLeaf</h1>
      <p className="text-xl text-gray-600 mb-6">Regisztráció</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex gap-4">
            <input 
                type="text" 
                value={lastName} 
                onChange={(e) => setLastName(e.target.value)} 
                placeholder="Vezetéknév" 
                required 
                autoFocus 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 transition-shadow"
            />
             <input 
                type="text" 
                value={firstName} 
                onChange={(e) => setFirstName(e.target.value)} 
                placeholder="Keresztnév" 
                required 
                className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 transition-shadow"
            />
        </div>
        <div><input type="text" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="Felhasználónév" required className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500"/></div>
        <div><input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email cím" required className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500"/></div>
        <div className="relative">
            <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Jelszó" required className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 pr-12"/>
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-green-600">
              {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </button>
        </div>
        <div className="relative">
            <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Jelszó megerősítése" required className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-500 pr-12"/>
            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute inset-y-0 right-0 px-4 flex items-center text-gray-500 hover:text-green-600">
              {showConfirmPassword ? <EyeSlashIcon /> : <EyeIcon />}
            </button>
        </div>
        
        {error && <p className="text-red-500 text-sm pt-2">{error}</p>}

        <div className="pt-2">
            <button type="submit" disabled={isLoading} className="w-12 h-12 bg-green-700 text-white rounded-full flex items-center justify-center mx-auto hover:bg-green-800 disabled:bg-gray-400">
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <ArrowIcon className="w-5 h-5" />
              )}
            </button>
        </div>
      </form>
    </div>
  );
};

export default Register;
